import React, { useEffect, useState } from "react";
import { App, Button, Divider, Input } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import AppControl from "../../components/";
import Card from "../../components/controls/Card";
import { getScreen, saveScreen, updateScreen } from "../../store/slice/screens";

const emptyChild = { title: "", key: "" };

const validationSchema = Yup.object().shape({
  key: Yup.string().required("Key is required"),
  title: Yup.string().required("Title is required"),
  children: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required("Permission title is required"),
      key: Yup.string().required("Permission key is required"),
    }),
  ),
});

function ScreenAddEdit() {
  const [title, setTitle] = useState("CREATE SCREEN");
  const { id } = useParams();
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { screen, isLoading } = useSelector((state) => state.screens);

  useEffect(() => {
    if (id) {
      setTitle("EDIT SCREEN");
      dispatch(getScreen(id));
    }
  }, [id, dispatch]);

  const initialValues =
    id && screen
      ? {
          key: screen.key || "",
          title: screen.title || "",
          children: screen.children || [],
        }
      : { key: "", title: "", children: [] };

  const onSubmit = async (values) => {
    const thunkCreator = id ? updateScreen : saveScreen;
    const payload = id ? { ...values, id } : values;
    const resultAction = await dispatch(thunkCreator(payload));

    if (thunkCreator.fulfilled.match(resultAction)) {
      message.success(resultAction.payload.message);
      navigate("/screen");
    } else {
      message.error(resultAction.error.message);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Card title={title}>
          <Form>
            <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("key")}
                  placeholder="e.g. associate"
                  status={
                    formik.touched.key && formik.errors.key ? "error" : ""
                  }
                />
                {formik.touched.key && formik.errors.key && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.key}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("title")}
                  placeholder="e.g. Associate"
                  status={
                    formik.touched.title && formik.errors.title ? "error" : ""
                  }
                />
                {formik.touched.title && formik.errors.title && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.title}
                  </p>
                )}
              </div>
            </div>

            <Divider titlePlacement="left" plain>
              <span className="text-sm font-medium text-gray-600">
                Permissions
              </span>
            </Divider>

            <FieldArray name="children">
              {({ push, remove }) => (
                <div className="space-y-2">
                  {formik.values.children.map((child, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-2 gap-2 items-center"
                    >
                      <Input
                        value={child.title}
                        placeholder="Title (e.g. View)"
                        status={
                          formik.touched.children?.[i]?.title &&
                          formik.errors.children?.[i]?.title
                            ? "error"
                            : ""
                        }
                        onChange={(e) =>
                          formik.setFieldValue(
                            `children[${i}].title`,
                            e.target.value,
                          )
                        }
                      />
                      <div className="flex gap-2">
                        <Input
                          value={child.key}
                          placeholder="Key (e.g. Permissions.Associate.View)"
                          status={
                            formik.touched.children?.[i]?.key &&
                            formik.errors.children?.[i]?.key
                              ? "error"
                              : ""
                          }
                          onChange={(e) =>
                            formik.setFieldValue(
                              `children[${i}].key`,
                              e.target.value,
                            )
                          }
                        />
                        <Button
                          danger
                          size="small"
                          icon={<FiTrash2 />}
                          onClick={() => remove(i)}
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    size="small"
                    icon={<FiPlus className="inline mr-1" />}
                    onClick={() => push({ ...emptyChild })}
                  >
                    Add Permission
                  </Button>
                </div>
              )}
            </FieldArray>

            <div className="border-t-2 border-neutral-100 pt-4 mt-6">
              <AppControl
                control="button"
                type="submit"
                name="Save"
                size="md"
                color="orange"
                disabled={!formik.isValid || isLoading}
                loading={isLoading}
              />
              <AppControl
                control="button"
                type="button"
                name="Cancel"
                size="md"
                color="blue"
                onClick={() => navigate("/screen")}
              />
            </div>
          </Form>
        </Card>
      )}
    </Formik>
  );
}

export default ScreenAddEdit;
